import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    imageUrl = "imageUrl",
    mobimageUrl = "mobimageUrl",
    altText = "altText",
    imageLink = "imageLink",
    cssClass = "cssClass",
    openInNewWindow = "openInNewWindow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.imageUrl]: string;
        [fields.mobimageUrl]: string;
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.cssClass]: string;
        [fields.openInNewWindow]: boolean;
    };
}

export interface ImageStyles {
    wrapper?: InjectableCss;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    imageLink?: LinkPresentationProps;
}

export const imageStyles: ImageStyles = {
    image: {
        css: css`
            max-width: 100%;
            height: auto;
        `,
    },
    img: {
        css: css`
            max-width: 100%;
            height: auto;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`margin-top: -7px; padding-bottom: 0px; margin-left: 0px;`,
                    css`margin-top: -7px; padding-bottom: 0px; margin-left: 0px;`,
                    null,
                    null,
                    null,
                ],)}
        `,
    },
};

export const ADIAppsImage: FC<OwnProps> = ({ fields }) => {
    const { url } = useGetLink(fields.imageLink);
    const styles = useMergeStyles("image", imageStyles);
    const [isMob, setIsMob] = React.useState(false);


    const onWindowResize = () => {
        if (window.innerWidth <= 767) {
            setIsMob(true);
        } else {
            setIsMob(false);
        }
    };


    React.useEffect(() => {
        if (typeof window !== "undefined") {
            if (window.innerWidth <= 767) {
                setIsMob(true);
            } else {
                setIsMob(false);
            }
        }
        onWindowResize();
        if (typeof window !== "undefined") {
            window.addEventListener("resize", onWindowResize);
        }

        return () => {
            if (typeof window !== "undefined") {
                window.removeEventListener("resize", onWindowResize);
            }
        };
    }, []);



    const image = <Img src={isMob && fields.mobimageUrl ? fields.mobimageUrl : fields.imageUrl} altText={fields.altText} loading="lazy" {...styles.img} />;

    return (
        <StyledWrapper {...styles.wrapper} className={fields.cssClass}>
            {url ? (
                <Link href={url} {...styles.imageLink} target={fields.openInNewWindow ? "_blank" : ""}>
                    {image}
                </Link>
            ) : (
                image
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ADIAppsImage,
    definition: {
        group: "Basic",
        icon: "Image",
        displayName: "ADI Footer Exclusive Banner",
        fieldDefinitions: [
            {
                name: fields.imageUrl,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                name: fields.mobimageUrl,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                name: fields.altText,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.imageLink,
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.cssClass,
                displayName: "CSS class name",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.openInNewWindow,
                editorTemplate: "CheckboxField",
                fieldType: "Translatable",
                defaultValue: false,
            },
        ],
    },
};

export default widgetModule;
